// ---------------------------------------------------------------------------
// HomeAligns website — single content & configuration source.
// Product facts here were verified against the live HomeAligns app
// (Service Hub 1600) on 26 Oct 2026. Prices, policies and features must be
// updated here (or in the app, then here) when the product changes.
// ---------------------------------------------------------------------------

// The existing HomeAligns app entry point. All CTAs on the website lead here.
// At launch, swap this env value to the production app URL (e.g. https://app.homealigns.com).
const APP_BASE = process.env.REACT_APP_HOMEALIGNS_APP_URL;

export const APP_LINKS = {
  getStarted: `${APP_BASE}/get-started`,
  login: `${APP_BASE}/login`,
  registerCustomer: `${APP_BASE}/register?role=customer`,
  registerVendor: `${APP_BASE}/register?role=vendor`,
};

export const COMPANY = {
  product: "HomeAligns",
  operator: "Dwellsync Technologies Private Limited",
  cin: "U63119MR2026PTC481755",
  address:
    "Elite J 1503, Palava Phase II, LSG Kalyan Thane, Khoni, Thane, Kalyan, Maharashtra – 421204, India",
  supportEmail: "support@homealigns.com",
  domain: "homealigns.com",
};

export const NAV_LINKS = [
  { label: "Features", to: "/features" },
  { label: "Home Services", to: "/home-services" },
  { label: "Pricing", to: "/pricing" },
  { label: "For Service Pros", to: "/for-service-professionals" },
  { label: "About", to: "/about" },
  { label: "Help", to: "/help" },
];

export const PROBLEMS = [
  {
    title: "Scattered warranty cards",
    text: "Purchase invoices, warranty cards and insurance papers live in drawers, inboxes and old WhatsApp chats — impossible to find when something breaks.",
  },
  {
    title: "Forgotten maintenance",
    text: "AC servicing, RO filter changes and geyser check-ups slip by until they become expensive repairs.",
  },
  {
    title: "Unclear asset values",
    text: "Nobody tracks what household assets are worth today, so insurance, resale and budgeting decisions are guesswork.",
  },
  {
    title: "Hard-to-find service pros",
    text: "Finding a trustworthy plumber or electrician still means asking around and hoping for the best.",
  },
];

export const FEATURES = [
  {
    icon: "Boxes",
    title: "Household & asset management",
    text: "Manage appliances, vehicles and properties with individual product and purchase histories — everything you own, organised in one place.",
  },
  {
    icon: "Building2",
    title: "Multiple-property management",
    text: "Track more than one home — your flat, your parents' house, a rented property — with records kept neatly per property.",
  },
  {
    icon: "FileText",
    title: "Documents & warranties",
    text: "Store invoices, receipts, manuals and warranty documents privately against each asset, and find them in seconds when you need them.",
  },
  {
    icon: "ShieldCheck",
    title: "Warranty, AMC & free servicing",
    text: "Track warranty and extended-warranty expiry, free servicing entitlements and AMC renewals — never miss a coverage window.",
  },
  {
    icon: "ClipboardList",
    title: "Complete service history",
    text: "Maintain full service and repair histories with replacement parts and servicing expenses, and download an appliance's service-history PDF.",
  },
  {
    icon: "ScanLine",
    title: "AI scanning & recommendations",
    text: "AI scans invoices, receipts and warranty documents to fill details for your review, and suggests maintenance dates — always labelled separately from manufacturer-recommended intervals.",
  },
  {
    icon: "BellRing",
    title: "Smart reminders",
    text: "Reminders 10 days before, 5 days before and on the due date — for servicing, warranties, AMCs, insurance, PUC, rent, bills and more.",
  },
  {
    icon: "Wallet",
    title: "Money management",
    text: "Rent, utility bills, expenses, budgets and insights in the Money tab — available on every plan, with limits that grow with your membership.",
  },
  {
    icon: "Users",
    title: "Family sharing",
    text: "Invite family members to your household and choose what each person can see or manage with per-member permissions.",
  },
  {
    icon: "Wrench",
    title: "Home-services marketplace",
    text: "Discover service professionals, browse services, request bookings, discuss requirements and receive quotations where supported.",
  },
  {
    icon: "Repeat",
    title: "Buy & sell household products",
    text: "List and discover pre-owned appliances, furniture and electronics. Direct buyer–seller chat and offers are coming soon.",
  },
  {
    icon: "PieChart",
    title: "Expense insights",
    text: "Category-wise charts, monthly trends, budget utilisation, property-wise and family-member-wise analysis, and yearly reports.",
  },
];

export const REMINDER_TYPES = [
  "Appliance servicing",
  "Warranty & extended warranty",
  "Free servicing windows",
  "AMC renewals",
  "Insurance & PUC renewals",
  "Rent payments",
  "Utility bills",
  "Other household reminders",
];

export const MONEY_AREAS = [
  {
    icon: "Landmark",
    title: "Rent management",
    points: [
      "Rent payable if you live in a rented home, rent receivable if you rent one out",
      "Rental agreements and their expiry dates",
      "Security deposit records",
      "Rent payment history with full, partial and overdue tracking",
    ],
  },
  {
    icon: "Zap",
    title: "Utility bills",
    points: [
      "Electricity, piped gas, LPG, water, internet & broadband, and other recurring bills",
      "Due dates, bill history and payment records",
      "Tracking and record management — HomeAligns does not process bill payments",
    ],
  },
  {
    icon: "Wallet",
    title: "Personal & family expenses",
    points: [
      "Personal and shared household expense recording",
      "Equal and custom expense splitting",
      "Custom categories, recurring expenses and monthly budgets",
      "Monthly summaries and property-wise tracking",
    ],
  },
  {
    icon: "PieChart",
    title: "Expense insights",
    points: [
      "Interactive category-wise pie & doughnut charts",
      "Monthly spending trends and budget utilisation",
      "Property-wise and family-member-wise analysis",
      "Yearly reports",
    ],
  },
];

export const SERVICE_CATEGORIES = [
  { icon: "Wrench", name: "Plumbing" },
  { icon: "Zap", name: "Electrical" },
  { icon: "Sparkles", name: "Cleaning" },
  { icon: "Paintbrush", name: "Painting" },
  { icon: "Hammer", name: "Carpentry" },
  { icon: "Bug", name: "Pest Control" },
  { icon: "AirVent", name: "AC Repair" },
  { icon: "Refrigerator", name: "Refrigerator Repair" },
  { icon: "WashingMachine", name: "Washing Machine Repair" },
  { icon: "Droplets", name: "RO Services" },
  { icon: "Flame", name: "Geyser Services" },
];

export const CUSTOMER_PLANS = [
  {
    name: "Free",
    price: "₹0",
    period: "free forever",
    blurb: "Start organising your home today.",
    highlight: false,
    benefits: [
      "Household & asset management within the standard allowance",
      "Product history timeline",
      "Manual purchase and service records",
      "Warranty and extended-warranty date tracking",
      "Basic document management within standard limits",
      "Basic maintenance tracking & reminders",
      "1 active rent agreement — payable or receivable",
      "3 utility billers",
      "Personal & household expense recording with standard categories",
      "Monthly expense totals & numerical category breakdown",
      "Full access to the Money tab",
      "Home-services marketplace access",
    ],
  },
  {
    name: "Plus",
    price: "₹999",
    period: "for 6 months",
    blurb: "For households that want more room to grow.",
    highlight: true,
    benefits: [
      "Everything in Free",
      "3 active rent agreements",
      "Unlimited utility billers",
      "Free-servicing & AMC tracking",
      "AI-assisted maintenance-date estimates",
      "AI invoice, receipt & bill scanning",
      "Family expense sharing with equal & custom splits",
      "Custom expense categories",
      "Recurring expense tracking & reminders",
      "Overall monthly budget",
      "Month-to-month spending comparison",
      "Interactive category-wise charts & monthly trends",
      "Monthly Money Report PDF download",
      "Optional monthly report email — off by default",
      "Extended asset, document & family-sharing limits",
    ],
  },
  {
    name: "Pro",
    price: "₹1,999",
    period: "for 6 months",
    blurb: "The full HomeAligns experience.",
    highlight: false,
    benefits: [
      "Everything in Plus",
      "Unlimited active rent agreements",
      "Advanced category-wise budgets",
      "Property-wise expense analytics & charts",
      "Family-member-wise spending analysis",
      "Advanced yearly expense reports",
      "Deeper household & property expense insights",
      "Savings Goals — exclusive to Pro (coming soon)",
      "Highest asset, document & family-sharing entitlements",
    ],
  },
];

export const VENDOR_PLANS = [
  {
    name: "Starter",
    price: "₹4,999",
    period: "for 6 months",
    blurb: "Establish your presence and start receiving service opportunities.",
    highlight: false,
  },
  {
    name: "Pro",
    price: "₹8,999",
    period: "per year",
    blurb: "For established professionals and growing teams.",
    highlight: true,
  },
];

export const FAQS = [
  {
    q: "What is HomeAligns?",
    a: "HomeAligns is a household management platform for India. It helps you manage your household, assets, documents, expenses and bills, stay on top of maintenance with smart reminders, and connect with trusted home-service professionals — all from one place. The Money tab (rent, utility bills, expenses and insights) is included on every plan, including Free.",
  },
  {
    q: "Can I manage more than one property?",
    a: "Yes. HomeAligns is built for multiple properties — you can keep each home's assets, rent and expense records, documents and reminders separate but manage them all from one account.",
  },
  {
    q: "How does warranty and document tracking work?",
    a: "You attach warranties, invoices, insurance policies and other documents to the asset they belong to. Files are stored privately and are only visible to you and the family members you choose to share with. HomeAligns reminds you before warranties expire and policies come up for renewal.",
  },
  {
    q: "Can my family use the same household?",
    a: "Yes. You can invite family members and set a permission level for each person, so everyone sees only what you decide to share.",
  },
  {
    q: "What can I buy or sell on the marketplace?",
    a: "You can list and discover pre-owned appliances, furniture, kitchen appliances, electronics and other household products. Direct buyer–seller chat, product offers and negotiation are coming soon. Until then, listings include an enquiry option and you control what contact details you share.",
  },
  {
    q: "What does AI scanning do?",
    a: "When you choose Scan with AI, the photo or document you select is read by AI to fill in asset details for you to review before saving. AI can make mistakes, so always check the details. AI features are optional and need your consent. AI-suggested maintenance dates are always shown separately from manufacturer-recommended service intervals.",
  },
  {
    q: "Are the asset values shown in the app accurate?",
    a: "Depreciation values and AI-estimated market values are automatic estimates for your information only. They are not valuations, appraisals or guaranteed sale prices — actual resale prices depend on condition, demand, location and negotiation.",
  },
  {
    q: "How do service bookings work?",
    a: "You can book approved service professionals directly, or send a service request that matching approved and subscribed providers in your city can respond to. Bookings and responses are not guaranteed — providers decide whether to respond, and you choose whether to proceed. Your contact details are only shared with a provider when you decide to share them.",
  },
  {
    q: "How much does HomeAligns cost?",
    a: "For households there is a Free plan (₹0), Plus at ₹999 for 6 months and Pro at ₹1,999 for 6 months. The Money tab — rent, utility bills, expenses and insights — is available on every plan; your membership determines individual feature access and limits. AI scanning is included on Plus and Pro, and Savings Goals are an upcoming Pro-exclusive feature. For service professionals, Starter is ₹4,999 for 6 months and Pro is ₹8,999 per year for new purchases. Payments currently run in Test Mode, so no real money is charged.",
  },
  {
    q: "How do I join as a service professional?",
    a: "Register as a Service Pro, complete your business profile and verification, and choose a vendor plan. Your profile becomes visible to customers once HomeAligns has approved it and your subscription is active. Subscriptions buy platform access and listed features — they do not guarantee leads, bookings or revenue. HomeAligns does not deduct commission from bookings.",
  },
  {
    q: "Is my household data private?",
    a: "Your household records, documents and photos are private to your account and the family members you share with. Service providers cannot browse your assets, properties or documents, and HomeAligns does not sell personal data or use advertising SDKs. You can download your data or delete your account at any time from Profile → Privacy & data in the app.",
  },
  {
    q: "How do I delete my account or request my data?",
    a: "In the app, go to Profile → Privacy & data to download a copy of your data or delete your account. Deletion deactivates your account immediately and erases it 7 days after your request unless you cancel. You can also email support@homealigns.com for help.",
  },
  {
    q: "How do I contact support?",
    a: "Email support@homealigns.com. The team aims to respond within 30 days for privacy requests, and sooner for general support. You can also raise support tickets inside the app.",
  },
];

// Real app screenshots for the product-demo section. Each entry is a genuine
// capture of the HomeAligns app stored in /public/screenshots/. When this list
// is empty the section falls back to code-built illustrated mockups.
export const APP_SCREENSHOTS = [
  // { src: "/screenshots/dashboard.png", alt: "HomeAligns household dashboard", label: "Household dashboard" },
];

// Real store URLs go here once the apps are published. While a value is null,
// the Coming Soon section renders a non-clickable informational badge only.
export const STORE_LINKS = {
  ios: null,     // e.g. "https://apps.apple.com/in/app/homealigns/idXXXXXXXXXX"
  android: null, // e.g. "https://play.google.com/store/apps/details?id=com.homealigns"
};

export const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Add your home",
    text: "Create your household, add your properties and record the assets you own — scan invoices with AI to fill details in seconds.",
  },
  {
    step: "02",
    title: "Stay organised",
    text: "Attach documents, track values and depreciation, and set reminders for warranties, insurance and maintenance.",
  },
  {
    step: "03",
    title: "Get things done",
    text: "Discover approved service professionals, book the one you choose, and manage every household need from one place.",
  },
];
